'use client'

// The dots, in one place so the corner button and the tabs cannot drift.
//
// Stacked with a slight overlap rather than spaced out: two things waiting
// should read as one cluster you deal with in one visit, not as two separate
// errands. Newest-mattering first, fixed order, so they never reshuffle
// under the eye.

import { kawaii } from '../../lib/ui/theme'
import { NOTICE, activeNotices } from '../../lib/ui/notices'

/**
 * @param {object} notices  the store's { offerings, games, you }
 * @param {number} size     dot diameter
 * @param {boolean} stacked overlap them (a corner badge) or space them (inline)
 */
function NoticeDots({ notices, size = 13, stacked = true }) {
  const kinds = activeNotices(notices)
  if (!kinds.length) return null

  return (
    <span
      className="inline-flex items-center"
      style={{ pointerEvents: 'none' }}
      aria-label={kinds.map((k) => NOTICE[k].label).join(', ')}
    >
      {kinds.map((k, i) => (
        <span
          key={k}
          title={NOTICE[k].label}
          style={{
            width: size,
            height: size,
            borderRadius: '50%',
            background: NOTICE[k].color,
            border: `2px solid ${kawaii.ink}`,
            // The overlap is what makes them one badge. Later dots sit on
            // top so the leftmost stays fully visible.
            marginLeft: i === 0 ? 0 : (stacked ? -size * 0.38 : 4),
            zIndex: i,
            boxSizing: 'border-box',
          }}
        />
      ))}
    </span>
  )
}

/** The badge version: absolutely placed on the corner of a round button. */
export function NoticeBadge({ notices }) {
  const kinds = activeNotices(notices)
  if (!kinds.length) return null
  return (
    <span style={{ position: 'absolute', top: -5, right: -5, display: 'flex' }}>
      <NoticeDots notices={notices} />
    </span>
  )
}

/** One dot, for a tab that owns a single kind. */
export function NoticeDot({ kind, size = 8 }) {
  const meta = NOTICE[kind]
  if (!meta) return null
  return (
    <span
      title={meta.label}
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: meta.color,
        border: `1.5px solid ${kawaii.ink}`,
        flexShrink: 0,
        boxSizing: 'border-box',
      }}
    />
  )
}

export default NoticeDots
